/**
 * Vue 支付 Composable
 */

import { computed, type ComputedRef } from '@vue/reactivity'
import type { PaymentService } from '../../../core/services/PaymentService'
import type {
  Package,
  Order,
  PaymentQRCode,
  OrderDetail,
  Coupon,
  CreateOrderParams,
  CreateOrderResponse,
  ActivateCodeParams,
  ActivateCodeResponse,
  GetCouponResponse,
  MiniProgramPaymentParams,
  MiniProgramPaymentResponse,
  MiniProgramVirtualPaymentParams,
  MiniProgramVirtualPaymentResponse,
  MiniProgramVirtualPaymentExecuteOptions,
  UserTokens
} from '../../../types'
import type { PaymentPollingHandle } from '../../../core/services/PaymentService'

export interface UsePaymentReturn {
  // 状态
  packages: ComputedRef<Package[]>
  currentOrder: ComputedRef<Order | null>
  paymentQRCode: ComputedRef<PaymentQRCode | null>
  coupons: ComputedRef<Coupon[]>

  // 计算属性
  hasOrder: ComputedRef<boolean>
  hasPaymentQRCode: ComputedRef<boolean>
  availableCoupons: ComputedRef<Coupon[]>

  // 方法
  getPackages: () => Promise<Package[]>
  createOrder: (params: CreateOrderParams) => Promise<CreateOrderResponse>
  getOrderDetail: (orderId: number | string) => Promise<OrderDetail>
  getPaymentQRCode: (params: {
    plan_type: number
    goods_id: number
    coupon_code?: string
  }) => Promise<PaymentQRCode>
  startPaymentPolling: (
    orderId: string,
    onSuccess?: () => void,
    onFailed?: () => void
  ) => Promise<void>
  stopPaymentPolling: () => void
  activateCode: (params: ActivateCodeParams) => Promise<ActivateCodeResponse>
  getCoupon: (couponId: number) => Promise<GetCouponResponse>
  getCouponByCode: (code: string) => Promise<GetCouponResponse>
  getUserCoupons: () => Promise<Coupon[]>
  getMiniProgramPayment: (params: MiniProgramPaymentParams) => Promise<MiniProgramPaymentResponse>
  getMiniProgramVirtualPayment: (
    params: MiniProgramVirtualPaymentParams
  ) => Promise<MiniProgramVirtualPaymentResponse>
  canUseMiniProgramVirtualPayment: () => boolean
  getMiniProgramLoginCode: () => Promise<string>
  requestMiniProgramVirtualPayment: (
    params: MiniProgramVirtualPaymentResponse
  ) => Promise<void>
  fetchMiniProgramVirtualPaymentParams: (
    orderId: string,
    options?: MiniProgramVirtualPaymentExecuteOptions
  ) => Promise<MiniProgramVirtualPaymentResponse>
  createMiniProgramVirtualPaymentPolling: (
    orderId: string,
    options?: { interval?: number; maxAttempts?: number }
  ) => PaymentPollingHandle
  executeMiniProgramVirtualPayment: (
    orderId: string,
    options?: MiniProgramVirtualPaymentExecuteOptions
  ) => Promise<void>
  createOrderAndGetQRCode: (params: CreateOrderParams) => Promise<{
    order: CreateOrderResponse
    qrCode: PaymentQRCode
  }>
  clearPaymentInfo: () => void
  clearAll: () => void
  getPackageById: (id: number) => Package | undefined
  getPackagesByType: (type: string) => Package[]
  getTokenPackages: () => Promise<Package[]>
  getPointPackages: () => Promise<Package[]>
  getUserTokens: () => Promise<UserTokens>
  getSubscribePackages: () => Promise<Package[]>
  cancelSubscribe: (subscribeId: string) => Promise<void>
}

/**
 * 创建支付 Composable
 */
export function createUsePayment(paymentService: PaymentService): () => UsePaymentReturn {
  return function usePayment(): UsePaymentReturn {
    const paymentStore = paymentService.getPaymentStore()

    // 响应式状态
    const packages = computed(() => paymentStore.packages)
    const currentOrder = computed(() => paymentStore.currentOrder)
    const paymentQRCode = computed(() => paymentStore.paymentQRCode)
    const coupons = computed(() => paymentStore.coupons)

    // 计算属性（直接返回 store 的 computed，避免双重包装）
    const hasOrder = paymentStore.hasOrder
    const hasPaymentQRCode = paymentStore.hasPaymentQRCode
    const availableCoupons = paymentStore.availableCoupons

    // 方法
    const getPackages = () => paymentService.getPackages()
    const createOrder = (params: CreateOrderParams) => paymentService.createOrder(params)
    const getOrderDetail = (orderId: number | string) => paymentService.getOrderDetail(orderId)
    const getPaymentQRCode = (params: {
      plan_type: number
      goods_id: number
      coupon_code?: string
    }) => paymentService.getPaymentQRCode(params)
    const startPaymentPolling = (orderId: string, onSuccess?: () => void, onFailed?: () => void) =>
      paymentService.startPaymentPolling(orderId, onSuccess, onFailed)
    const stopPaymentPolling = () => paymentService.stopPaymentPolling()
    const activateCode = (params: ActivateCodeParams) => paymentService.activateCode(params)
    const getCoupon = (couponId: number) => paymentService.getCoupon(couponId)
    const getCouponByCode = (code: string) => paymentService.getCouponByCode(code)
    const getUserCoupons = () => paymentService.getUserCoupons()
    const getMiniProgramPayment = (params: MiniProgramPaymentParams) =>
      paymentService.getMiniProgramPayment(params)
    const getMiniProgramVirtualPayment = (params: MiniProgramVirtualPaymentParams) =>
      paymentService.getMiniProgramVirtualPayment(params)
    const canUseMiniProgramVirtualPayment = () =>
      paymentService.canUseMiniProgramVirtualPayment()
    const getMiniProgramLoginCode = () => paymentService.getMiniProgramLoginCode()
    const requestMiniProgramVirtualPayment = (params: MiniProgramVirtualPaymentResponse) =>
      paymentService.requestMiniProgramVirtualPayment(params)
    const fetchMiniProgramVirtualPaymentParams = (
      orderId: string,
      options?: MiniProgramVirtualPaymentExecuteOptions
    ) => paymentService.fetchMiniProgramVirtualPaymentParams(orderId, options)
    const createMiniProgramVirtualPaymentPolling = (
      orderId: string,
      options?: { interval?: number; maxAttempts?: number }
    ) => paymentService.createMiniProgramVirtualPaymentPolling(orderId, options)
    const executeMiniProgramVirtualPayment = (
      orderId: string,
      options?: MiniProgramVirtualPaymentExecuteOptions
    ) => paymentService.executeMiniProgramVirtualPayment(orderId, options)
    const createOrderAndGetQRCode = (params: CreateOrderParams) =>
      paymentService.createOrderAndGetQRCode(params)
    const clearPaymentInfo = () => paymentService.clearPaymentInfo()
    const clearAll = () => paymentService.clearAll()
    const getPackageById = (id: number) => paymentService.getPackageById(id)
    const getPackagesByType = (type: string) => paymentService.getPackagesByType(type)
    const getTokenPackages = () => paymentService.getTokenPackages()
    const getPointPackages = () => paymentService.getPointPackages()
    const getUserTokens = () => paymentService.getUserTokens()
    const getSubscribePackages = () => paymentService.getSubscribePackages()
    const cancelSubscribe = (subscribeId: string) => paymentService.cancelSubscribe(subscribeId)

    return {
      // 状态
      packages,
      currentOrder,
      paymentQRCode,
      coupons,

      // 计算属性
      hasOrder,
      hasPaymentQRCode,
      availableCoupons,

      // 方法
      getPackages,
      createOrder,
      getOrderDetail,
      getPaymentQRCode,
      startPaymentPolling,
      stopPaymentPolling,
      activateCode,
      getCoupon,
      getCouponByCode,
      getUserCoupons,
      getMiniProgramPayment,
      getMiniProgramVirtualPayment,
      canUseMiniProgramVirtualPayment,
      getMiniProgramLoginCode,
      requestMiniProgramVirtualPayment,
      fetchMiniProgramVirtualPaymentParams,
      createMiniProgramVirtualPaymentPolling,
      executeMiniProgramVirtualPayment,
      createOrderAndGetQRCode,
      clearPaymentInfo,
      clearAll,
      getPackageById,
      getPackagesByType,
      getTokenPackages,
      getPointPackages,
      getUserTokens,
      getSubscribePackages,
      cancelSubscribe
    }
  }
}
